import { cloneDeep, isEmpty } from 'lodash'

import { ProvenanceTargetType } from 'components/attacks/create/create-attack-types'
import {
  isContainerTargetingStrategy,
  isHostTargetingStrategy,
  isTargetUnhealthy
} from 'components/attacks/create/targets/target-utils'
import { isKubernetesAttack } from 'components/attacks/utils/kubernetes'
import { stepsToGraphAPI } from 'components/scenarios/utils/graph'
import duration from 'duration'
import { type Client } from 'types/api/attacks/client'
import { type Execution } from 'types/api/attacks/details/execution'
import {
  type InfraAttack,
  type InfraCommand,
  type InfraTarget
} from 'types/api/attacks/details/infra-attack'
import {
  type AttackCliArgs,
  AttackTypeCategory,
  InfraStrategyType
} from 'types/api/attacks/details/targeting'
import { type K8sAttack } from 'types/api/kubernetes/attacks'
import { type ProvidersToServices } from 'types/api/providers/all'
import {
  type InfraAttackNode,
  type Scenario,
  type StepAttack
} from 'types/api/scenarios/scenario'
import { type Metadata } from 'types/metadata'
import { keysToCamelCase } from 'utils/obj'
import {
  createIlfiAttackSearchMap,
  createK8AttackSearchMap
} from 'utils/searchStringGenerate'

/**
 * `cpu` and `latency` are the only supported attacks with validation, but their results can be used to gauge the likelihood of attack failures from other types.
 * This structure is a mapping of attacks types to the relevant validation attack types they can leverage.
 */
const ATTACK_VALIDATION_DEPENDENCY_INDEX = {
  cpu: ['cpu'],
  disk: ['cpu'],
  io: ['cpu'],
  memory: ['cpu'],
  process_killer: ['cpu'],
  shutdown: ['cpu'],
  // time_travel uses blackhole to drop NTP traffic, use latency to gauge its health
  time_travel: ['cpu', 'latency'],
  blackhole: ['cpu', 'latency'],
  dns: ['cpu', 'latency'],
  latency: ['cpu', 'latency'],
  packet_loss: ['cpu', 'latency']
}

export function areAttackDependenciesHealthy(
  attackType: string,
  target: Client
): boolean {
  // only check error states if a target is considered unhealthy
  if (!isTargetUnhealthy(target)) {
    return true
  }

  if (target.validation_results?.results) {
    const validationDependencies =
      ATTACK_VALIDATION_DEPENDENCY_INDEX[attackType] ?? []
    const matchingErrs = target.validation_results.results.filter(
      (result) =>
        validationDependencies?.includes(result.attack_type) &&
        result.status === 'ERROR'
    )
    return matchingErrs.length < 1
  }
  return true
}

export function getResolvedHosts(attack: Partial<InfraAttack>): string[] {
  return attack?.infra_target?.resolvedHosts ?? attack?.targets ?? []
}

export function getResolvedContainers(attack: Partial<InfraAttack>): string[] {
  return (
    attack?.infra_target?.resolvedContainers ?? attack?.target_containers ?? []
  )
}

/**
 * Informs initial parameters for attack creation form.
 * @param  scenarioAttack Last attack step previously selected.
 * @return Formatted attack to be compatible with
 *  createInfraInitialTarget function in infraInitialFormState.js.
 */
export function scenarioAttackToAdHocAttack(
  scenarioAttack: Pick<
    StepAttack,
    'attackConfiguration' | 'continueOnEmptyTargets' | 'includeNewTargets'
  > & {
    targetDefinition: InfraTarget
    impactDefinition: InfraCommand
    metrics: Record<string, unknown>
  }
) {
  const {
    attackConfiguration,
    continueOnEmptyTargets,
    includeNewTargets,
    targetDefinition,
    metrics
  } = scenarioAttack

  const { targets, command, sampling } = attackConfiguration

  const targetingStrategy = targets?.[0]?.targetingStrategy
  const targetType = targetingStrategy?.type

  const infraTargetStrategy = {
    ...targetingStrategy
  }

  if (sampling?.type === 'Even') {
    infraTargetStrategy.percentage = sampling.percent
  } else {
    infraTargetStrategy.count = sampling.count
  }

  const convertedAttack = {
    targetAll: false, // FE only
    args: keysToCamelCase(command).infraCommandArgs?.cliArgs,
    attack_configuration: attackConfiguration,
    providers: command.infraCommandArgs?.providers,
    traffic_impact_mapping: command.infraCommandArgs?.traffic_impact_mapping,
    target_type: targetType,
    infra_command: {
      commandType: command.infraCommandType
    },
    infra_target: {
      strategy: infraTargetStrategy,
      strategyType: 'Random', // Scenarios do not support Exact targeting
      targetType: targetType
    },
    metrics,
    includeNewTargets: includeNewTargets ?? true,
    continueOnEmptyTargets: Boolean(continueOnEmptyTargets)
  }

  // Depending on `targetType`, we check whether `tags` or `labels` are empty,
  // and pass through our UI-only `targetAll` param to the state we provide our
  // InfraAttackForm to correctly toggle the `<InputSwitch />` so users are not blocked from rerunning attacks
  if (
    targetDefinition.strategyType !== InfraStrategyType.Exact &&
    ((targetType === ProvenanceTargetType.ProcessData &&
      isEmpty(targetingStrategy?.tags) &&
      isEmpty(targetingStrategy?.multiSelectTags)) ||
      (targetType === ProvenanceTargetType.Container &&
        isEmpty(targetingStrategy?.labels) &&
        isEmpty(targetingStrategy?.multiSelectLabels)))
  ) {
    convertedAttack.targetAll = true
  }

  return convertedAttack
}

/**
 * Take the data for an attack, or an execution of an attack, and convert that into the format
 * used on the create scenario form
 */
export function convertAttackToScenarioState(
  execution: Partial<Execution | InfraAttack>,
  name: string,
  description: string,
  hypothesis: string
): { scenario: Partial<Scenario> } {
  const steps = [
    {
      delay: 5,
      attacks: [
        {
          // @ts-ignore
          // TODO: fix union type
          attackConfiguration: execution.attack_configuration,
          impactDefinition: {
            commandArgs: {
              cliArgs: execution?.args ?? [],
              trafficImpactMapping: execution.traffic_impact_mapping,
              providers: execution.providers ?? []
            },
            commandType: execution?.infra_command?.commandType ?? ''
          },
          targetDefinition: {
            targetType: execution?.infra_target?.targetType ?? '',
            strategy: execution?.infra_target?.strategy ?? {
              percentage: 100
            },
            strategyType: execution?.infra_target?.strategyType ?? 'Random'
          }
        }
      ]
    }
  ]

  return {
    scenario: {
      name,
      description,
      hypothesis,
      steps,
      graph: stepsToGraphAPI(steps)
    }
  }
}

/**
 * Checks if an attack is a network attack
 *
 * @param attack An attack data object
 * @param metadata attack metadata list
 */
export function isNetworkAttack(
  attack: InfraAttack | InfraAttackNode,
  metadata: Metadata
): boolean {
  const infraCommandType =
    attack?.attack_configuration?.command?.infraCommandType
  return Boolean(
    infraCommandType &&
      metadata?.find(
        (attackMeta) =>
          attackMeta.name === infraCommandType &&
          attackMeta.type === AttackTypeCategory.Network
      )
  )
}

/**
 * Filter IPs out of a comma-separated string of IPs
 *
 * @param ipList string A comma-separated list of IPs
 * @param removeIPs string[] An array of IPs to remove from the string
 */
export function filterIPListString(ipList: string, removeIPs: string[]) {
  return ipList
    .split(',')
    .filter((ipAddress) => !removeIPs.includes(ipAddress))
    .join(',')
}

/**
 * loop over all arguments, find ip args, and then filter their IP lists
 *
 * @param attackArgs string[] An array of arguments for the attack
 * @param ipAddressArgs string[] An array of individual argument names
 * @param removeIPs string[] An array of IPs to remove from the string
 * @returns {string[]}
 */
export function filterIPArguments(
  attackArgs: AttackCliArgs,
  ipAddressArgs: string[],
  removeIPs: string[]
) {
  const filteredArgs = Array.from(attackArgs).map((a) => a.toString())

  for (let argNum = 0; argNum < filteredArgs.length; argNum++) {
    if (ipAddressArgs.includes(filteredArgs[argNum])) {
      // this is an ip address arg, so the next arg is the string of IPs we need to filter
      filteredArgs[argNum + 1] = filterIPListString(
        filteredArgs[argNum + 1],
        removeIPs
      )

      // now check if this list of IPs is empty and empty out both args so we can filter the empties out later
      if (!filteredArgs[argNum + 1]) {
        filteredArgs[argNum] = ''
      }

      // now increment past the string arg and continue looping
      // (the user can pass more than one IP address arg via the CLI)
      argNum++
    }
  }

  // remove any empty arguments
  return filteredArgs.filter(Boolean)
}

/**
 * Cleans up completed attack object data when it needs to be reused for an attack rerun or creating a scenario
 *
 * @param attack {} An attack data object
 * @param metadata [] attack metadata list
 */
export function copyAttackForReuse(
  attack: Record<string, any>,
  metadata: Metadata
): Partial<InfraAttack> {
  const copy = cloneDeep(attack)
  const attackConfiguration = copy?.attack_configuration

  const targetingStrategy = attackConfiguration?.targets?.[0]?.targetingStrategy

  const isHostAttack = isHostTargetingStrategy(targetingStrategy)
  const isContainerAttack = isContainerTargetingStrategy(targetingStrategy)

  // Depending on the attack target type, we check whether `tags/multiSelectTags` or `labels/multiSelectLabels` are empty, and pass through our UI-only `targetAll` param to the state we provide our InfraAttackForm to correctly toggle the `<InputSwitch />` so users are not blocked from rerunning attacks
  if (
    (isHostAttack &&
      isEmpty(targetingStrategy?.tags) &&
      isEmpty(targetingStrategy?.multiSelectTags) &&
      isEmpty(targetingStrategy?.ids)) ||
    (isContainerAttack &&
      isEmpty(targetingStrategy?.labels) &&
      isEmpty(targetingStrategy?.multiSelectLabels) &&
      isEmpty(targetingStrategy?.ids))
  ) {
    copy.targetAll = true
  }

  // if we're copying an attack that used Host Tags, then we'll have a list of resolved IPs in the IP address arg
  // these need to be removed from the list so that we can resolve fresh IPs the next time the attack runs
  // TODO: This populates legacy fields in relative to the newer attack_configuration object referenced above. This should be removed or updated as part of the Create Attack form refactor.  See https://github.com/gremlin/webapp/pull/4864
  if (isNetworkAttack(attack as InfraAttack, metadata)) {
    copy.trafficImpactMapping = attack.traffic_impact_mapping

    // pull the short and long args for IP address lists from attack metadata so we're not hardcoding them here
    const networkAttack = metadata.find(
      (attackMeta) => attackMeta.name === attack.infra_command.commandType
    )
    const ipAddressArg = networkAttack.args.find((arg) => arg.type === 'IP')
    const ipAddressArgs = [ipAddressArg.short, ipAddressArg.long]

    // check if this network attack has IPs that were resolved from Host Tags, and filter them out
    const ips = attack?.dynamically_resolved_ips

    if (ips) {
      // remove from args (used when re-running an attack)
      if (attack?.args?.length > 0) {
        copy.args = filterIPArguments(copy.args, ipAddressArgs, ips)
      }

      // remove it from this arg list also, just in case
      if (attack?.infra_command?.commandArgs?.cliArgs?.length > 0) {
        copy.infra_command.commandArgs.cliArgs = filterIPArguments(
          copy.infra_command.commandArgs.cliArgs,
          ipAddressArgs,
          ips
        )
      }

      // remove from infra_command parsed args (used when creating a scenario)
      if (attack?.infra_command?.commandArgs?.ipAddresses?.length > 0) {
        copy.infra_command.commandArgs.ipAddresses = filterIPListString(
          attack.infra_command.commandArgs.ipAddresses,
          ips
        )

        if (copy.infra_command.commandArgs.ipAddresses.length < 1) {
          // there's no IPs left, so remove this property entirely
          delete copy.infra_command.commandArgs.ipAddresses
        }
      }
    }
  }

  return copy
}

export function getSearchMapAttack(data: InfraAttack | K8sAttack): string[] {
  if (isKubernetesAttack(data as K8sAttack)) {
    return createK8AttackSearchMap(data)
  }

  return createIlfiAttackSearchMap(data)
}

export function getAttackDurationReadable(
  args: AttackCliArgs = [] as unknown as AttackCliArgs
) {
  const attackFlagIndex =
    (args.indexOf('-l') > -1 && args.indexOf('-l')) ||
    (args.indexOf('--length') > -1 && args.indexOf('--length'))

  return attackFlagIndex ? duration(args[attackFlagIndex + 1]) : ''
}

/** Returns flattened array of sorted Provider values */
export function getProviderList(
  providersToServices: ProvidersToServices | undefined
) {
  return Object.values(providersToServices ?? {})
    .flatMap((provider) => provider)
    .sort((a, b) => a.localeCompare(b))
}
